import Async from '../Async';

class AttendeeExport
{
    /**
     * @method constructor
     */
    constructor()
    {
        // Declare and initialize
        this.modal        = $('#export-attendees-modal');
        this.confirmation = this.modal.find('[data-attendee-export-confirmation]');
        this.loading      = this.modal.find('[data-attendee-export-loading]');
        this.error        = this.modal.find('[data-attendee-export-error]');
        this.confirm      = this.modal.find('[data-attendee-export-confirm]');
        this.close        = this.modal.find('[data-attendee-export-close]');
        this.async        = new Async();
        this.busy         = false;

        // Nothing to export on this page
        if(!this.modal.length)
            return;

        this.init();
    }

    /**
     * Bind attendee export interactions.
     *
     * @method init
     * @since 0.0.1
     * @return void
     */
    init()
    {
        // Start the export after confirmation
        this.confirm.on('click', () =>
        {
            this.requestExport();
        });

        // Do not let the modal close while an export is being prepared
        this.modal.on('hide.bs.modal', (event) =>
        {
            if(this.busy)
                event.preventDefault();
        });

        // Always restore the confirmation view after the modal is closed
        this.modal.on('hidden.bs.modal', () =>
        {
            this.reset();
        });
    }

    /**
     * Request the attendee CSV from the server.
     *
     * @method requestExport
     * @since 0.0.1
     * @return void
     */
    requestExport()
    {
        if(this.busy)
            return;

        this.setBusy(true);

        this.async.post('export-attendees', {
            params: {
                event_id:   this.modal.attr('data-event-id'),
                recur_date: this.modal.attr('data-recur-date')
            },
            success: {
                callback: (response) =>
                {
                    this.handleResponse(response);
                }
            },
            fail: {
                callback: () =>
                {
                    this.showError('Unable to prepare the attendee export.');
                }
            }
        });
    }

    /**
     * Handle a completed export request.
     *
     * @method handleResponse
     * @since 0.0.1
     * @param object response is the normalized provider response
     * @return void
     */
    handleResponse(response)
    {
        if(!response || !response.success)
        {
            this.showError(
                response && response.message
                    ? response.message
                    : 'Unable to prepare the attendee export.'
            );
            return;
        }

        // Declare and initialize
        let result   = response.data || {};
        let filename = result.filename || '';
        let content  = result.content;

        if(!filename || 'string' !== typeof content)
        {
            this.showError('The attendee export could not be downloaded.');
            return;
        }

        // Start the browser download before closing the modal
        this.download(filename, content);

        // Unlock the modal and close it after the download begins
        this.busy = false;
        this.modal.removeAttr('aria-busy');
        this.close.prop('disabled', false);
        this.confirm.prop('disabled', false);
        this.modal.modal('hide');
    }

    /**
     * Toggle the modal between confirmation and loading states.
     *
     * @method setBusy
     * @since 0.0.1
     * @param bool busy indicates whether an export is in progress
     * @return void
     */
    setBusy(busy)
    {
        this.busy = busy;

        this.confirmation.toggleClass('d-none', busy);
        this.loading.toggleClass('d-none', !busy);
        this.error.addClass('d-none').empty();
        this.close.prop('disabled', busy);
        this.confirm.prop('disabled', busy);

        if(busy)
            this.modal.attr('aria-busy', 'true');
        else
            this.modal.removeAttr('aria-busy');
    }

    /**
     * Show an export failure and allow the user to retry or close the modal.
     *
     * @method showError
     * @since 0.0.1
     * @param string message is the error message to display
     * @return void
     */
    showError(message)
    {
        this.setBusy(false);
        this.error.text(message).removeClass('d-none');
    }

    /**
     * Reset the export modal to its initial state.
     *
     * @method reset
     * @since 0.0.1
     * @return void
     */
    reset()
    {
        this.setBusy(false);
    }

    /**
     * Trigger a browser download for the generated CSV.
     *
     * @method download
     * @since 0.0.1
     * @param string filename is the suggested CSV filename
     * @param string content is the generated CSV content
     * @return void
     */
    download(filename, content)
    {
        // Declare and initialize
        let blob = new Blob([content], {
            type: 'text/csv;charset=utf-8;'
        });
        let url  = window.URL.createObjectURL(blob);
        let link = document.createElement('a');

        // Build our temporary download link
        link.href          = url;
        link.download      = filename;
        link.style.display = 'none';

        // Trigger the download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the temporary object URL
        window.setTimeout(() =>
        {
            window.URL.revokeObjectURL(url);
        }, 1000);
    }
}

export default AttendeeExport;
